// Lo que muestra el dashboard de métricas de Instagram, sin pantalla —
// ADR-2026-10-29-dashboard-de-metricas-de-instagram. Se prueba en `metricas.test.ts`.

import type { CuentaDeInstagram, PostDeInstagram } from '../../../../lib/instagramMetricas';

/** Los períodos que se pueden elegir. Meta no deja sumar personas únicas más allá de 30 días. */
export const PERIODOS = [7, 28] as const;
export type Periodo = (typeof PERIODOS)[number];

const entero = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });
const unDecimal = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 });

/**
 * 1.234 · 12,3 mil · 1,2 M. Sin dato, una raya (nunca un 0 que no es). El "mil" va a mano: el
 * formato compacto del navegador dice "k" o "mil" según dónde corra.
 */
export function formatearNumero(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${unDecimal.format(n / 1_000_000)} M`;
  if (abs >= 10_000) return `${unDecimal.format(n / 1_000)} mil`;
  return entero.format(n);
}

/** "+12 %" / "−5 %" / null si no hay con qué comparar. */
export function textoDeVariacion(pct: number | null | undefined): string | null {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
  const redondo = Math.abs(pct) >= 10 ? Math.round(pct) : Math.round(pct * 10) / 10;
  if (redondo === 0) return '0 %';
  const signo = redondo > 0 ? '+' : '−';
  return `${signo}${String(Math.abs(redondo)).replace('.', ',')} %`;
}

/**
 * El instante de una fecha de Instagram, en ms (NaN si no se puede leer). Tolera el
 * `+0000` sin dos puntos que manda Meta: Safari no lo entiende y la edge vieja lo pasaba tal cual.
 */
export function instanteDe(fecha: string | null | undefined): number {
  if (!fecha) return NaN;
  return new Date(fecha.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')).getTime();
}

export interface Tarjeta {
  clave: string;
  etiqueta: string;
  valor: number | null;
  /** Contra el período anterior. */
  variacion: number | null;
  /** Una línea chica abajo del número. */
  detalle: string | null;
}

/** Los posts que salieron en los últimos `dias`. */
export function postsDelPeriodo(posts: PostDeInstagram[], dias: number, ahora = Date.now()): PostDeInstagram[] {
  const desde = ahora - dias * 24 * 60 * 60 * 1000;
  return posts.filter((p) => {
    const t = instanteDe(p.fecha);
    return Number.isFinite(t) && t >= desde;
  });
}

/**
 * Las cinco tarjetas de arriba: seguidores, alcance, vistas, interacciones y posts del período.
 * `posts` es null mientras la lista no llegó: esa tarjeta espera y las demás no.
 */
export function tarjetasDeLaCuenta(
  cuenta: CuentaDeInstagram,
  posts: PostDeInstagram[] | null,
  ahora = Date.now(),
): Tarjeta[] {
  const metrica = (m: string) => cuenta.metricas.find((x) => x.metrica === m);
  const nuevos = metrica('seguidores_nuevos')?.valor ?? null;
  const seFueron = metrica('dejaron_de_seguir')?.valor ?? null;
  const delPeriodo = posts ? postsDelPeriodo(posts, cuenta.dias, ahora) : null;
  const deMetrica = (clave: string, etiqueta: string): Tarjeta => ({
    clave,
    etiqueta,
    valor: metrica(clave)?.valor ?? null,
    variacion: metrica(clave)?.variacion_pct ?? null,
    detalle: null,
  });
  return [
    {
      clave: 'seguidores',
      etiqueta: 'Seguidores',
      valor: cuenta.seguidores,
      variacion: null,
      detalle: detalleDeSeguidores(nuevos, seFueron),
    },
    deMetrica('reach', 'Cuentas alcanzadas'),
    deMetrica('views', 'Vistas'),
    deMetrica('total_interactions', 'Interacciones'),
    {
      clave: 'posts',
      etiqueta: 'Posts',
      valor: delPeriodo ? delPeriodo.length : null,
      variacion: null,
      // La lista trae los últimos 30: con más que eso en el período, el número sería de menos.
      detalle: delPeriodo && posts && delPeriodo.length >= posts.length && posts.length >= 30 ? 'o más' : null,
    },
  ];
}

function detalleDeSeguidores(nuevos: number | null, seFueron: number | null): string | null {
  const partes = [
    nuevos !== null ? `+${formatearNumero(nuevos)} nuevos` : null,
    seFueron ? `${formatearNumero(seFueron)} se fueron` : null,
  ].filter(Boolean);
  return partes.length ? partes.join(' · ') : null;
}

const esVideo = (p: PostDeInstagram) => p.tipo === 'reel' || p.formato === 'video';

/** El número que va sobre la miniatura: vistas en los videos, cuentas alcanzadas en el resto. */
export function numeroPrincipal(p: PostDeInstagram): { valor: number | null; etiqueta: string } {
  if (esVideo(p) && p.vistas !== null) return { valor: p.vistas, etiqueta: 'vistas' };
  if (p.alcance !== null) return { valor: p.alcance, etiqueta: 'alcanzadas' };
  if (p.vistas !== null) return { valor: p.vistas, etiqueta: 'vistas' };
  return { valor: p.me_gusta, etiqueta: 'me gusta' };
}

export const ORDENES = [
  { clave: 'recientes', etiqueta: 'Recientes' },
  { clave: 'vistas', etiqueta: 'Más vistos' },
  { clave: 'guardados', etiqueta: 'Más guardados' },
] as const;
export type Orden = (typeof ORDENES)[number]['clave'];

/** Ordena sin mutar. Los que no tienen el dato van al final: no cuentan como 0. */
export function ordenarPosts(posts: PostDeInstagram[], orden: Orden): PostDeInstagram[] {
  const copia = [...posts];
  if (orden === 'recientes') return copia.sort((a, b) => (b.fecha ?? '').localeCompare(a.fecha ?? ''));
  return copia.sort((a, b) => {
    const x = a[orden];
    const y = b[orden];
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return y - x;
  });
}

/** Los números de un post, en el orden en que se leen. Los que Meta no mandó no aparecen. */
export function numerosDelPost(p: PostDeInstagram): Array<{ etiqueta: string; valor: string }> {
  const filas: Array<[string, number | null]> = [
    ['Me gusta', p.me_gusta],
    ['Comentarios', p.comentarios],
    ['Cuentas alcanzadas', p.alcance],
    ['Vistas', p.vistas],
    ['Guardados', p.guardados],
    ['Compartidos', p.compartidos],
  ];
  const out = filas.filter(([, v]) => v !== null).map(([etiqueta, v]) => ({ etiqueta, valor: formatearNumero(v) }));
  if (p.segundos_promedio_vistos !== null) {
    out.push({
      etiqueta: 'Segundos vistos (promedio)',
      valor: `${String(p.segundos_promedio_vistos).replace('.', ',')} s`,
    });
  }
  return out;
}

/** Instagram completa los números hasta 48 h después: un post más nuevo que eso todavía suma. */
export function esReciente(fecha: string | null, ahora = Date.now()): boolean {
  const t = instanteDe(fecha);
  return Number.isFinite(t) && ahora - t < 48 * 60 * 60 * 1000;
}

/** "recién" · "hace 5 min" · "hace 2 h". */
export function haceCuanto(desde: number, ahora = Date.now()): string {
  const min = Math.floor((ahora - desde) / 60_000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  return `hace ${Math.floor(min / 60)} h`;
}

/** Cuánto hay que esperar entre un "Actualizar" y el siguiente: cada lectura gasta el cupo de Meta. */
export const ESPERA_PARA_ACTUALIZAR_MS = 2 * 60_000;

export function sePuedeActualizar(leidoEn: number | null, ahora = Date.now()): boolean {
  return leidoEn === null || ahora - leidoEn >= ESPERA_PARA_ACTUALIZAR_MS;
}

/** "lunes 27 de octubre" (con el año si no es el actual). */
export function fechaDelPost(fecha: string | null, ahora = new Date()): string {
  const d = new Date(instanteDe(fecha));
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(d.getFullYear() !== ahora.getFullYear() ? { year: 'numeric' } : {}),
  });
}
